import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Lock,
  KeyRound,
  Fingerprint,
  X,
  CheckCircle2,
  AlertCircle,
  ShieldAlert,
} from 'lucide-react';

interface SetPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'setup' | 'change' | 'disable';
  onSuccess?: () => void;
}

export const SetPinModal: React.FC<SetPinModalProps> = ({
  isOpen,
  onClose,
  mode,
  onSuccess,
}) => {
  const {
    language,
    enableAppLock,
    disableAppLock,
    changeAppLockPin,
    isBiometricsAvailable,
  } = useApp();

  const isBn = language === 'bn';

  const [oldPin, setOldPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [enableBiometricsCheckbox, setEnableBiometricsCheckbox] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (mode === 'setup') {
      if (newPin.length !== 4) {
        setError(isBn ? 'পিন অবশ্যই ঠিক ৪ সংখ্যার হতে হবে!' : 'PIN must be exactly 4 digits!');
        return;
      }
      if (newPin !== confirmPin) {
        setError(isBn ? 'কনফার্ম পিন মিলছে না!' : 'PINs do not match!');
        return;
      }

      setIsSubmitting(true);
      const res = await enableAppLock(newPin, enableBiometricsCheckbox && isBiometricsAvailable);
      setIsSubmitting(false);

      if (res.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(res.error || (isBn ? 'পিন সেট করতে সমস্যা হয়েছে।' : 'Failed to set PIN.'));
      }
    } else if (mode === 'change') {
      if (oldPin.length !== 4) {
        setError(isBn ? 'বর্তমান ৪-সংখ্যার পিন দিন!' : 'Enter your current 4-digit PIN!');
        return;
      }
      if (newPin.length !== 4) {
        setError(isBn ? 'নতুন পিন অবশ্যই ৪ সংখ্যার হতে হবে!' : 'New PIN must be exactly 4 digits!');
        return;
      }
      if (newPin !== confirmPin) {
        setError(isBn ? 'নতুন কনফার্ম পিন মিলছে না!' : 'New PINs do not match!');
        return;
      }

      setIsSubmitting(true);
      const res = await changeAppLockPin(oldPin, newPin);
      setIsSubmitting(false);

      if (res.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(res.error || (isBn ? 'পিন পরিবর্তন করা সম্ভব হয়নি।' : 'Failed to change PIN.'));
      }
    } else if (mode === 'disable') {
      if (oldPin.length !== 4) {
        setError(isBn ? 'বর্তমান ৪-সংখ্যার পিন দিন!' : 'Enter your current 4-digit PIN!');
        return;
      }

      setIsSubmitting(true);
      const res = await disableAppLock(oldPin);
      setIsSubmitting(false);

      if (res.success) {
        onSuccess?.();
        onClose();
      } else {
        setError(res.error || (isBn ? 'ভুল পিন কোড!' : 'Incorrect PIN!'));
      }
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'setup':
        return isBn ? 'নতুন অ্যাপ লক পিন সেট করুন' : 'Set Up App Lock PIN';
      case 'change':
        return isBn ? 'অ্যাপ লক পিন পরিবর্তন করুন' : 'Change App Lock PIN';
      case 'disable':
        return isBn ? 'অ্যাপ লক বন্ধ করুন' : 'Turn Off App Lock';
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="p-5 border-b border-slate-200/80 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              {mode === 'disable' ? <ShieldAlert className="w-5 h-5 text-rose-500" /> : <Lock className="w-5 h-5" />}
            </div>
            <div>
              <h3 className={`text-base font-black text-slate-900 dark:text-white ${isBn ? 'font-bangla' : ''}`}>
                {getTitle()}
              </h3>
              <p className="text-[11px] text-slate-500">
                {mode === 'disable'
                  ? (isBn ? 'লক বন্ধ করতে বর্তমান পিন দিন' : 'Enter current PIN to disable app lock')
                  : (isBn ? '৪-সংখ্যার গোপন পিন নম্বর প্রদান করুন' : 'Enter a 4-digit security PIN')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-rose-600 dark:text-rose-400 text-xs font-bold flex items-center gap-2 animate-shake">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Old PIN (For Change or Disable mode) */}
          {(mode === 'change' || mode === 'disable') && (
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                <span>{isBn ? 'বর্তমান ৪-ডিজিট পিন' : 'Current 4-Digit PIN'}</span>
              </label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={oldPin}
                onChange={(e) => setOldPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••"
                className="w-full text-center text-xl tracking-[0.5em] font-mono py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
              />
            </div>
          )}

          {/* New PIN & Confirm (For Setup or Change mode) */}
          {(mode === 'setup' || mode === 'change') && (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>{mode === 'change' ? (isBn ? 'নতুন ৪-ডিজিট পিন' : 'New 4-Digit PIN') : (isBn ? '৪-ডিজিট পিন দিন' : 'Enter 4-Digit PIN')}</span>
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  className="w-full text-center text-xl tracking-[0.5em] font-mono py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus={mode === 'setup'}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  <span>{isBn ? 'পিন পুনরায় নিশ্চিত করুন' : 'Confirm PIN'}</span>
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••"
                  className="w-full text-center text-xl tracking-[0.5em] font-mono py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Optional Biometric Checkbox on setup */}
              {mode === 'setup' && isBiometricsAvailable && (
                <label className="flex items-center gap-3 p-3 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/40 rounded-2xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableBiometricsCheckbox}
                    onChange={(e) => setEnableBiometricsCheckbox(e.target.checked)}
                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 accent-emerald-600"
                  />
                  <div className="flex items-center gap-2">
                    <Fingerprint className="w-5 h-5 text-emerald-500" />
                    <div>
                      <span className="text-xs font-bold text-slate-900 dark:text-white block">
                        {isBn ? 'ফিঙ্গারপ্রিন্ট আনলক চালু করুন' : 'Enable Fingerprint Unlock'}
                      </span>
                      <span className="text-[10px] text-slate-500 block">
                        {isBn ? 'ডিভাইসের সেন্সর দিয়ে দ্রুত খুলুন' : 'Unlock easily using device fingerprint'}
                      </span>
                    </div>
                  </div>
                </label>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              {isBn ? 'বাতিল' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 py-2.5 rounded-xl text-xs font-bold text-white shadow-md flex items-center justify-center gap-1.5 transition-all ${
                mode === 'disable'
                  ? 'bg-rose-600 hover:bg-rose-700 shadow-rose-500/20'
                  : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20'
              } disabled:opacity-50`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>
                {isSubmitting
                  ? (isBn ? 'প্রসেসিং...' : 'Processing...')
                  : mode === 'disable'
                  ? (isBn ? 'লক বন্ধ করুন' : 'Disable Lock')
                  : (isBn ? 'নিশ্চিত করুন' : 'Confirm')}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
